import React from "react";
import { NavLink } from "react-router-dom";
import { Send, FileText, CalendarCheck, MessageSquare, Plus, Home } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useDashT } from "../../lib/dashboardStrings";

/**
 * Mobile bottom navigation.
 *
 * Four hot destinations always one tap away — the same slots the user opens
 * ~90% of the time from the hamburger. Hidden on lg+ where the sidebar
 * already provides the shortcut, and hidden entirely for admins (whose tool
 * surface is too broad for a four-slot bar).
 *
 * The bar is fixed at the bottom, sits over the safe-area on iOS (via
 * env(safe-area-inset-bottom)), and always renders four equal-width items so
 * the tap targets are 25% of screen each.
 */

const talentItems = (dt) => [
  { to: "/talent/gigs", label: dt("Gigs"), icon: Send, match: (p) => p.startsWith("/talent/gigs") },
  { to: "/talent/offers", label: dt("Offers"), icon: FileText, match: (p) => p === "/talent/offers" || p === "/offers" },
  { to: "/talent/bookings", label: dt("Bookings"), icon: CalendarCheck, match: (p) => p.startsWith("/talent/bookings") },
  { to: "/messages", label: dt("Messages"), icon: MessageSquare, match: (p) => p.startsWith("/messages") },
];

const clientItems = (dt) => [
  { to: "/dashboard", label: dt("Home"), icon: Home, match: (p) => p === "/dashboard" },
  { to: "/offers", label: dt("Offers"), icon: FileText, match: (p) => p.startsWith("/offers") },
  { to: "/boka", label: dt("Book"), icon: Plus, primary: true, match: (p) => p.startsWith("/boka") },
  { to: "/client/bookings", label: dt("Bookings"), icon: CalendarCheck, match: (p) => p.startsWith("/client/bookings") },
  { to: "/messages", label: dt("Messages"), icon: MessageSquare, match: (p) => p.startsWith("/messages") },
];

const MobileBottomNav = () => {
  const { user } = useAuth();
  const dt = useDashT();
  const role = user?.role;
  if (!role || role === "admin") return null;
  const items = role === "client" ? clientItems(dt) : talentItems(dt);

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#1e1e1e] bg-[#0D0D0D]/95 backdrop-blur"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map(({ to, label, icon: Icon, match, primary }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={to === "/dashboard"}
              data-testid={`mobile-nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
              className={({ isActive }) => {
                const active = isActive || (match && match(window.location.pathname));
                if (primary) {
                  // Center CTA styled as a filled orange pill lifted above the bar.
                  return "flex flex-col items-center justify-center pt-1.5 pb-2 text-[10.5px] text-white";
                }
                return `flex flex-col items-center justify-center pt-2 pb-1.5 text-[10.5px] transition-colors ${
                  active ? "text-[#FF7A2F]" : "text-neutral-500 hover:text-neutral-300"
                }`;
              }}
            >
              {primary ? (
                <span className="w-10 h-10 -mt-3 rounded-full bg-[#FF5C00] flex items-center justify-center shadow-[0_4px_18px_rgba(255,92,0,0.35)]">
                  <Icon size={20} />
                </span>
              ) : (
                <Icon size={20} />
              )}
              <span className={`mt-0.5 leading-tight tracking-tight ${primary ? "text-[#FF7A2F] font-semibold" : ""}`}>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default MobileBottomNav;
